import { supabase } from "@/integrations/supabase/client";

// Define a consistent user profile interface
export interface UserProfile {
  id: string;
  username: string | null;
  email: string;
  display_name: string | null;
  balance: number;
  profile_pic: string | null;
  bio: string | null;
}

export const getUserProfile = async (userId: string): Promise<{ profile: UserProfile | null; error: string | null }> => {
  try {
    // First try to get from users table
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .maybeSingle(); // Use maybeSingle instead of single to handle not found cases better
    
    if (error && error.code !== 'PGRST116') {
      // Log and return error only if it's not a "no rows returned" error
      console.error("Error fetching user profile:", error);
      return { profile: null, error: error.message };
    }

    if (!data) {
      // If no profile in users table, try to get basic info from user_info view
      console.log("No user profile found in users table, checking user_info view");
      const { data: userInfo, error: userInfoError } = await supabase
        .from('user_info')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (userInfoError && userInfoError.code !== 'PGRST116') {
        console.error("Error fetching from user_info:", userInfoError);
        return { profile: null, error: userInfoError.message };
      }

      // If found in user_info, create a basic profile
      if (userInfo) {
        console.log("User found in user_info:", userInfo);
        const basicProfile: UserProfile = {
          id: userInfo.user_id,
          username: userInfo.username,
          email: userInfo.email,
          display_name: userInfo.display_name,
          balance: userInfo.balance || 0,
          profile_pic: null,
          bio: null
        };
        return { profile: basicProfile, error: null };
      }

      // If still not found, try to get from auth.users as a last resort
      console.log("No user found in user_info either, user may not have a profile");
      return { 
        profile: { 
          id: userId,
          email: "Unknown user", 
          display_name: "Unknown user",
          username: null,
          balance: 0,
          profile_pic: null,
          bio: null
        }, 
        error: "User profile not found" 
      };
    }

    // Ensure consistent profile format
    const profile: UserProfile = {
      id: data.id,
      username: data.username,
      email: data.email,
      display_name: data.display_name,
      balance: data.balance || 0,
      profile_pic: data.profile_pic,
      bio: data.bio || null
    };

    console.log("User profile loaded:", profile);
    return { profile, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getUserProfile:", e);
    return { profile: null, error: e.message };
  }
};

export const getUserPlan = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('user_plans')
      .select('*')
      .eq('user_id', userId)
      .single();
    
    if (error) {
      // If no record is found, don't treat it as an error
      if (error.code === 'PGRST116') {
        return { plan: null, error: null };
      }
      console.error("Error fetching user plan:", error);
      return { plan: null, error: error.message };
    }

    return { plan: data, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in getUserPlan:", e);
    return { plan: null, error: e.message };
  }
};

export const updateUserProfile = async (userId: string, profileData: Partial<UserProfile>) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .update(profileData)
      .eq('id', userId)
      .select()
      .single();
    
    if (error) {
      console.error("Error updating user profile:", error);
      return { profile: null, error: error.message };
    }

    return { profile: data, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Error in updateUserProfile:", e);
    return { profile: null, error: e.message };
  }
};
